// ─── Optimistic Concurrency Control (OCC) — client helpers ───────────────────
// The server rejects a protected update with HTTP 409 when the record changed
// after the client loaded it (a "lost update"). To opt in, the client preserves
// the record's `updatedAt` at load time and sends it back with the update; the
// server compares that token against the stored document before writing.
//
// Usage at an edit call site (the loaded record is already in scope):
//
//   dispatch(updateVehicle({ id, vehicleData: withVersion(payload, vehicle) }))
//
// On a 409 the thunk's rejected message carries a human-readable prompt to
// reload — surface it as-is, and use `isConflictError` if you want to force a
// refetch.

/** Any loaded record carries the version token we preserve at load time. */
export interface Versioned {
  updatedAt?: string;
}

/** Read the `updatedAt` version token off a loaded record, if present. */
function versionOf(original: unknown): string | undefined {
  const v = (original as { updatedAt?: unknown } | null | undefined)?.updatedAt;
  if (!v) return undefined;
  return typeof v === "string" ? v : new Date(v as string).toISOString();
}

/**
 * Merge the loaded record's `updatedAt` into an update payload so the backend
 * can enforce optimistic concurrency. A no-op when the original has no token,
 * so it is always safe to wrap a payload with this. `original` is typed as
 * `unknown` because loaded entities come in many shapes — only its `updatedAt`
 * is read.
 */
export function withVersion<T extends object>(
  data: T,
  original: unknown,
): T & { updatedAt?: string } {
  const updatedAt = versionOf(original);
  if (!updatedAt) return data;
  return { ...data, updatedAt };
}

/** Append the loaded record's `updatedAt` to a multipart FormData payload. */
export function appendVersion(form: FormData, original: unknown): FormData {
  const updatedAt = versionOf(original);
  if (updatedAt) form.append("updatedAt", updatedAt);
  return form;
}

export const CONFLICT_STATUS = 409;

/** True when an axios/thunk error represents an OCC version conflict. */
export function isConflictError(error: unknown): boolean {
  const e = error as { response?: { status?: number }; status?: number };
  return e?.response?.status === CONFLICT_STATUS || e?.status === CONFLICT_STATUS;
}
